<template>
  <bean-source-page
    ref="sourcePage"
    :type="type"
    :columns="columns"
    :form-columns="formColumns"
    :filter-columns="filterColumns"
    :actions="actions"
    :form="form"
    :title="$t('model.salesOrder')"
    resource="sales_orders"
    :batch-actions="batchActions"
    :table-props="{
      stripe: false,
    }"
    :action-buttons="['show']"
  >
    <template #action>
      <SalesOrdersForm v-if="canCreateSalesOrder" @success="handleRefresh" />
      <ExcelPreview ref="previewReceivablesRef" style="display: none;" v-bind="previewReceivablesProps" />
      <ExcelPreview ref="previewCommissionSourcesRef" style="display: none;" v-bind="previewCommissionSourcesProps" />
      <ExcelPreview ref="excelPreviewRef" style="display: none;" v-bind="previewExcelProps" />
      <bean-import-button
        can='sales_orders.create'
        buttonText="导入"
        url="/sales_orders/import"
        :loadingToast="$t('importLoadingToast')"
        :fileChangeHandler="handleImportFileChange('/sales_orders/import')"
      >
        <template slot="download">
          <el-popover trigger="hover">
            <div style="cursor: pointer;" @click="downloadImportTemplate('/sales_orders/import_template')">下载模板</div>
            <i class="el-icon-info btn-tip-trigger" slot="reference" @click.stop></i>
          </el-popover>
        </template>
      </bean-import-button>
    </template>
    <template #after-table>
      <SalesOrderSummary :summary="summary" />
    </template>
  </bean-source-page>
</template>

<script>
import { Vue, Component, Prop, Mixins, Watch } from 'vue-property-decorator';
import { filterColumnsExtend, formColumnsExtend } from '@/admin_grapes/sales_orders.js';
import { formColumnsExtend as salesLineItemsFormColumnsExtend } from '@/admin_entities/sales_line_item_detail.js';
import { columnsExtend as jushuitanOrderColumnsExtend } from '@/admin_entities/jushuitan_order_detail.js';
import { columnsExtend as adjustmentColumnsExtend } from '@/admin_entities/adjustment_detail.js';
import { columnsExtend as paymentAllocationColumnsExtend } from '@/admin_entities/payment_allocation_detail.js';
import { columnsExtend as salesOrderAllocationColumnsExtend } from '@/admin_entities/sales_order_allocation_detail.js';
import { columnsExtend as afterSaleAllocationColumnsExtend } from '@/admin_entities/after_sale_allocation_detail.js';
import { abilityService } from 'beans-admin-plugin';
import Utils from '@/components/utils.vue';
import _ from 'lodash';
import { displayText } from '@/custom_models_extend';
import ExcelPreview from '@/components/excel-preview.vue';
import { orderTableColumns, SalesLineItems } from '@/utils/order';
import SalesOrdersForm from './form.vue';
import moment from 'moment';
import DialogContent from '@/components/dialog-content.vue';
import SalesOrderSummary from '@/components/sales-order-summary.vue';

const orderTableColumnsKeyByProp = _.keyBy(orderTableColumns, 'prop');

@Component({
  components: {
    ExcelPreview,
    SalesOrdersForm,
    SalesOrderSummary,
  }
})
export default class SalesOrders extends Mixins(Vue, Utils) {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {
    is_transfer_existing: false,
    ordered_on: (new Date()).toISOString().split('T')[0]
  }

  previewExcelProps = {
    onRequest: () => {},
    filename: '在线销售跟进表.xlsx',
  }

  previewReceivablesProps = {
    onRequest: () => {},
    filename: '应收报表.xlsx',
  }

  previewCommissionSourcesProps = {
    onRequest: () => {},
    filename: '提成源表.xlsx',
  }

  summary = {}

  get canCreateSalesOrder() {
    return abilityService.can('sales_orders.create');
  }

  @Watch('$route.query')
  async fetchSummaryData() {
    const summary = await this.$request.get('/sales_orders/summary', { ...this.$route.query });
    this.summary = summary;
  }

  mounted() {
    this.fetchSummaryData();
  }

  handleRefresh() {
    this.$refs.sourcePage.fetchData();
    this.fetchSummaryData();
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  filterColumns() {
    const array = [
      {
        key: 'keyword',
        label: '关键字'
      },
      'number_cont',
      {
        prop: 'jushuitan_orders_o_id_eq',
        label: '内部单号',
        renderCell: {
          component: 'input',
        }
      },
      'kind_eq',
      'product_type_eq',
      'business_kind_eq',
      {
        key: 'customer_id_eq',
        renderCell: {
          props: {
            xRemotePreload: async () => {
              return (await this.$request.get('customers/read_options', { per_page: 100, ...this.customerOptionsFilter })).data.map(item => ({ value: item.id, label: item.name }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('customers/read_options', { keyword, ...this.customerOptionsFilter })).data.map(item => ({ value: item.id, label: item.name }));
            }
          }
        }
      },
      abilityService.can('users.read_options') && {
        key: 'user_id_eq',
        renderCell: {
          props: {
            clearable: true,
            xRemotePreload: async () => {
              return (await this.$request.get('users/read_options', { per_page: 100, ...this.userOptionsFilter })).data.map(item => ({ value: item.id, label: displayText(item) }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('users/read_options', { keyword, ...this.userOptionsFilter })).data.map(item => ({ value: item.id, label: displayText(item) }));
            }
          },
        }
      },
      'jushuitan_orders_shop_id_eq',
      { key: 'ordered_on_gteq', label: `${this.$t('salesOrder.orderedOn')}${this.$t('gteqDatetime')}` },
      { key: 'ordered_on_lteq', label: `${this.$t('salesOrder.orderedOn')}${this.$t('lteqDatetime')}` },
      {
        key: 'payment_reminder_due_status_eq',
        label: '付款提醒状态',
        renderCell: {
          component: 'select',
          props: {
            clearable: true,
            options: [
              { value: 'approaching', label: '即将到期（今天起3天内）' },
              { value: 'overdue_1_7', label: '逾期1-7天' },
              { value: 'overdue_7_plus', label: '逾期7天以上' },
            ]
          }
        }
      },
      { key: 'finished_on_not_null', label: '是否完结', renderCell: { props: { clearable: true } } },
      {
        key: 'finished_on_month_eq',
        label: '完结月份',
        renderCell: {
        component: 'datePicker',
        props: {
            valueFormat: 'yyyy-MM',
            type: 'month'
          }
        }
      },
      {
        prop: 'user_team_id_eq',
        label: '团队/小组',
        renderCell: {
          component: 'app-team-tree-select',
        }
      },
    ].filter(Boolean)

    return filterColumnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  columns(h, { type }) {
    return [
      type === 'show' && {
        prop: 'id',
        label: 'ID',
      },
      orderTableColumnsKeyByProp.number,
      orderTableColumnsKeyByProp.kind,
      orderTableColumnsKeyByProp.product_type,
      orderTableColumnsKeyByProp.business_kind,
      orderTableColumnsKeyByProp.customer,
      orderTableColumnsKeyByProp.ordered_on,
      {
        prop: 'is_transfer_existing',
        label: '是否调拨现货',
        renderCell: 'bool',
      },
      {
        prop: 'user',
        label: '用户',
        width: 120,
        renderCell(h, { props: { value } }) {
          return <bean-link keep-node to={{ name: 'users.show', params: { id: value.id } }}>{ value.screen_name }</bean-link>
        }
      },
      orderTableColumnsKeyByProp.sales_amount,
      {
        prop: 'cost',
        label: '成本',
        width: 120,
        renderCell: 'app-locale-string'
      },
      {
        prop: 'tax',
        label: '税额',
        width: 120,
        renderCell: 'app-locale-string'
      },
      this.roleKind !== 'salesman' && this.roleKind !== 'customer_service' && {
        prop: 'profit',
        label: '毛利',
        width: 120,
        renderCell: 'app-locale-string'
      },
      {
        prop: 'fully_shipped',
        label: '是否完全出货',
        width: 120,
        renderCell: 'bool'
      },
      {
        prop: 'jushuitan_orders_amount',
        label: '聚水潭订单总金额',
        width: 160,
        renderCell: (h, { props: { value, scope: { row } } }) => {
          if (type === 'show') {
            const tableData = _.flatMap(row.sales_line_items, salesLineItem => {

              return (salesLineItem.shipped_info || []).map(shippedInfo => ({
                amount: Number(shippedInfo.amount) || 0,
                o_id: shippedInfo.jushuitan_order_o_id
              }))
            });
            return <el-popover placement="right" width="200" trigger="hover">
              <bean-table
                height={null} value={tableData} columns={jushuitanOrderColumnsExtend(['o_id', 'amount'])} />
              <span slot="reference"><app-locale-string value={value} /></span>
            </el-popover>
          }
          return <app-locale-string value={value} />
        }
      },
      {
        prop: 'jushuitan_orders_real_freight',
        label: '聚水潭订单运费',
        width: 160,
        renderCell: (h, { props: { value, scope: { row } } }) => {
          if (type === 'show') {
            const tableData = _.flatMap(row.sales_line_items, salesLineItem => {

              return (salesLineItem.shipped_info || []).map(shippedInfo => ({
                real_freight: Number(shippedInfo.freight_amount) || 0,
                o_id: shippedInfo.jushuitan_order_o_id
              }))
            });
            return <el-popover placement="right" width="200" trigger="hover">
              <bean-table
                height={null} value={tableData} columns={jushuitanOrderColumnsExtend(['o_id', 'real_freight'])} />
              <span slot="reference"><app-locale-string value={value} /></span>
            </el-popover>
          }
          return <app-locale-string value={value} />
        }
      },
      {
        prop: 'jushuitan_after_sales_amount',
        label: '聚水潭订单退款金额',
        width: 160,
        renderCell: (h, { props: { value, scope: { row } } }) => {
          if (type === 'show') {
            const tableData = _.flatMap(row.sales_line_items, salesLineItem => {

              return (salesLineItem.shipped_info || []).map(shippedInfo => ({
                amount: Number(shippedInfo.after_sale_amount) || 0,
                o_id: shippedInfo.jushuitan_order_o_id
              }))
            });
            return <el-popover placement="right" width="200" trigger="hover">
              <bean-table
                height={null} value={tableData} columns={jushuitanOrderColumnsExtend(['o_id', 'amount'])} />
              <span slot="reference"><app-locale-string value={value} /></span>
            </el-popover>
          }
          return <app-locale-string value={value} />
        }
      },
      {
        prop: 'adjustments_amount',
        label: '调整金额',
        width: 120,
        renderCell: (h, { props: { value, scope: { row } } }) => {
          return <el-popover placement="right" width="100" trigger="hover">
            <bean-table height={null} value={row.adjustments} columns={adjustmentColumnsExtend(['amount', 'remark'])} />
            <span slot="reference"><app-locale-string value={value} /></span>
          </el-popover>
        }
      },
      {
        prop: 'sales_order_allocations_amount',
        label: '售后销售订单相关金额',
        renderCell: (h, { props: { value, scope: { row } } }) => {
          if (row.kind === 'after_sale') {
            return <el-popover placement="right" width="400" trigger="hover">
              <bean-table height={null} value={row.sales_order_allocations_as_source} columns={salesOrderAllocationColumnsExtend(['target', 'amount', 'allocated_on'])} />
              <span slot="reference"><app-locale-string value={value} /></span>
            </el-popover>
          } else {
            return <el-popover placement="right" width="400" trigger="hover">
              <bean-table height={null} value={row.sales_order_allocations_as_target} columns={salesOrderAllocationColumnsExtend(['source', 'amount', 'allocated_on'])} />
              <span slot="reference"><app-locale-string value={value} /></span>
            </el-popover>
          }
        }
      },
      {
        prop: 'after_sale_allocations',
        label: '售后已收金额分配',
        renderCell: (h, { props: { value } }) => {
          if (value && value.length > 0) {
            return <el-popover placement="right" width="400" trigger="hover">
              <bean-table height={null} value={value} columns={afterSaleAllocationColumnsExtend(['jushuitan_after_sale', 'received_amount'])} />
              <span slot="reference"><app-locale-string value={_.sumBy(value, 'received_amount')} /></span>
            </el-popover>
          }
          return <span>-</span>
        }
      },
      {
        prop: 'received_amount',
        label: '已收总额',
        width: 160,
        renderCell: (h, { props: { value, scope: { row } } }) => {
          return <el-popover placement="right" width="400" trigger="hover">
            <bean-table height={null} value={[...row.payment_allocations, ...row.payment_allocations_from_jushuitan_orders]} columns={paymentAllocationColumnsExtend(['payment', 'payable', 'amount'])} />
            <span slot="reference">
              <app-locale-string value={value} />
            </span>
          </el-popover>
        }
      },
      {
        prop: 'receivable_amount',
        label: '应收总额',
        width: 120,
        renderCell: 'app-locale-string'
      },
      {
        prop: 'unreceived_receivable_amount',
        label: '应收余额',
        width: 120,
        renderCell: 'app-locale-string'
      },
      {
        prop: 'remaining_amount',
        label: '订单余额',
        width: 120,
        renderCell: 'app-locale-string'
      },
      {
        prop: 'is_even',
        label: '是否账平',
        renderCell: 'bool',
        width: 120,
      },
      {
        prop: 'finished_on',
        label: '完结月份',
        renderCell: (h, { props: { value } }) => {
          if (!value) {
            return
          }
          const content = moment(value).format('YYYY-MM')
          return <span>{content}</span>
        }
      },
      {
        ...orderTableColumnsKeyByProp.sales_line_items,
        minWidth: 120 * (type === 'show' ? 6 : 5) + 30,
        renderCell: (h, { props: { value } }) => {
          return <SalesLineItems value={value} showImage={type === 'show'} showShippedInfo={type === 'show'} wrapperStyle={type === 'show' ? { width: '100%' } : {}} />;
        }
      },
      orderTableColumnsKeyByProp['jushuitan_orders'],
      {
        prop: 'remark',
        label: '备注',
        minWidth: 160,
        renderCell: (h, { props: { value } }) => {
          if (!value) {
            return null;
          }

          const onClick = (e, handler) => {
            e.stopPropagation();
            handler && handler();
          }

          return (
            <DialogContent
              title="备注"
              scopedSlots={{
                trigger: ({ open }) => (
                  <div class="pre-wrap text-overflow-3 cursor-pointer" onClick={(e) => onClick(e, open)}>{value}</div>
                )
              }}
            >
              <app-multiple-row-text value={value} />
            </DialogContent>
          )
        }
      },
      type === 'show' && {
        prop: 'created_at',
        label: '创建时间',
        minWidth: 160,
        renderCell: 'time'
      },
      type === 'show' && {
        prop: 'updated_at',
        label: '更新时间',
        minWidth: 160,
        renderCell: 'time'
      }
    ].filter(Boolean);
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - page type enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -RESTful api resource name
   */
  formColumns(h, { data, type, change }) {
    const array = [
      {
        key: 'customer_id',
        renderCell: {
          props: {
            xRemotePreload: async () => {
              return (await this.$request.get('customers/read_options', { per_page: 100, user_id_eq: this.currentUser.id })).data.map(item => ({ value: item.id, label: item.name }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('customers/read_options', { keyword, user_id_eq: this.currentUser.id })).data.map(item => ({ value: item.id, label: item.name }));
            }
          }
        }
      },
      'kind',
      'product_type',
      'is_transfer_existing',
      'ordered_on',
      {
        key: 'sales_line_items',
        renderCell: {
          component: 'nestForm',
          required: true,
          props: {
            columns: (object) => {
              return salesLineItemsFormColumnsExtend([
                {
                  key: 'style_number',
                  renderCell: {
                    component: (h, context) => {
                      return <el-input {...context}>
                        <el-button slot="append" icon="el-icon-search" onClick={async () => {
                          try {
                            const result = await this.$autoLoading(this.$request.get(`/feige/products/${context.props.value}`, {}, { $alert: false }))
                            object.price = result.sales_price
                            object.quantity = result.min_order_qty
                            change({ ...data })
                          } catch (error) {
                            // ignore error
                          }
                        }}></el-button>
                      </el-input>
                    }
                  }
                },
                'price',
                'quantity'
              ], { data: object, type: type })
            }
          }
        }
      },
      data.customer_id && {
        key: 'jushuitan_order_ids',
        renderCell: {
          component: (h, context) => {
            return <div>
              <bean-select {...context} />
              <el-button disabled={type !== 'new' || _.get(data, 'jushuitan_order_ids', []).length === 0 || _.get(data, 'sales_line_items', []).length > 0} type="primary" onClick={this.autoFillSalesLineItems}>自动填充销售订单商品</el-button>
            </div>
          },
          props: {
            clearable: true,
            xRemotePreload: async () => {
              return (await this.$request.get('jushuitan_orders/read_options', { customer_id_eq: data.customer_id })).data.map(item => ({ value: item.id, label: displayText(item) }));
            },
            xRemoteSearch: async (keyword) => {
              return (await this.$request.get('jushuitan_orders/read_options', { keyword, customer_id_eq: data.customer_id })).data.map(item => ({ value: item.id, label: displayText(item) }));
            }
          }
        }
      },
    ].filter(Boolean)

    return formColumnsExtend(array, { data: data, type: type })
  }

  async autoFillSalesLineItems() {
    const ids = _.get(this.$refs.sourcePage.state.data, 'jushuitan_order_ids', [])
    if (ids.length !== 0) {
      const result = await this.$autoLoading(this.$request.get('/jushuitan_orders/sales_line_items', { ids }))
      this.$refs.sourcePage.syncForm({
        sales_line_items: result.items
      })
    }
  }

  /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  actions({ row }, { remove, reload }) {
    return [
      row.can_update && !row.finished_on && {
        component: () => <SalesOrdersForm value={row} onSuccess={() => this.handleRefresh()} />
      },
      row.can_update && !row.finished_on && row.kind === 'after_sale' && {
        component: 'dialogFormButton',
        props: {
          label: '分配售后金额',
          columns: [{
            prop: 'sales_order_allocations_as_source',
            label: '售后金额分配',
            required: true,
            renderCell: {
              component: 'nestForm',
              required: true,
              props: {
                columns: [{
                  prop: 'target_id',
                  label: '非售后销售订单',
                  renderCell: {
                    component: 'select',
                    required: true,
                    props: {
                      xRemotePreload: async () => {
                        return (await this.$request.get('sales_orders/read_options', { kind_in: ['sample', 'bulk'], customer_id_eq: row.customer_id })).data.map(item => ({ value: item.id, label: item.number }));
                      },
                      xRemoteSearch: async (keyword) => {
                        return (await this.$request.get('sales_orders/read_options', { keyword, kind_in: ['sample', 'bulk'], customer_id_eq: row.customer_id })).data.map(item => ({ value: item.id, label: item.number }));
                      }
                    }
                  }
                }, {
                  prop: 'amount',
                  label: '金额',
                  renderCell: {
                    component: 'input',
                    required: true,
                    props: {
                      type: 'number'
                    }
                  }
                }, {
                  prop: 'allocated_on',
                  label: '分配日期',
                  renderCell: {
                    component: 'datePicker',
                    props: {
                      valueFormat: 'yyyy-MM-dd'
                    }
                  }
                }]
              }
            }
          }],
          value: row,
          submitHandler: async (value) => {
            await this.$autoLoading(this.$request.put(`/sales_orders/${row.id}`, { ...value }));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.can_update && row.kind === 'after_sale' && {
        component: 'dialogFormButton',
        props: {
          label: '分配售后已收金额',
          columns: [{
            prop: 'after_sale_allocations',
            label: '售后已收金额分配',
            required: true,
            renderCell: {
              component: 'nestForm',
              required: true,
              props: {
                columns: [{
                  prop: 'jushuitan_after_sale_id',
                  label: '聚水潭售后',
                  renderCell: {
                    component: 'select',
                    required: true,
                    props: {
                      xRemotePreload: async () => {
                        return (await this.$request.get('jushuitan_after_sales/read_options', { customer_id_eq: row.customer_id })).data.map(item => ({ value: item.id, label: displayText(item) }));
                      },
                      xRemoteSearch: async (keyword) => {
                        return (await this.$request.get('jushuitan_after_sales/read_options', { keyword, customer_id_eq: row.customer_id })).data.map(item => ({ value: item.id, label: displayText(item) }));
                      }
                    }
                  }
                }, {
                  prop: 'received_amount',
                  label: '分配已收金额',
                  renderCell: {
                    component: 'input',
                    required: true,
                    props: {
                      type: 'number'
                    }
                  }
                }]
              }
            }
          }],
          value: row,
          submitHandler: async (value) => {
            await this.$autoLoading(this.$request.put(`/sales_orders/${row.id}`, { ...value }));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.can_destroy && {
        component: 'confirmButton',
        props: {
          label: this.$t('destroy'),
          type: 'danger',
          can: 'sales_orders.destroy'
        },
        on: {
          confirm: async () => {
            await remove(row.id);
            this.fetchSummaryData();
          }
        }
      },
      row.can_update_finished && {
        component: 'dialogFormButton',
        props: {
          label: '更新完结信息',
          value: row,
          columns: formColumnsExtend(['ordered_on', { key: 'finished_on', renderCell: { required: true } }], { data: { finished_on: row.finished_on }, type: 'edit' }),
          submitHandler: async (value) => {
            await this.$autoLoading(this.$request.put(`/sales_orders/${row.id}/update_finished`, value));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.can_cancel_finished && {
        component: 'confirmButton',
        props: {
          label: '取消完结',
          type: 'danger',
          can: 'sales_orders.cancel_finished'
        },
        on: {
          confirm: async () => {
            await this.$autoLoading(this.$request.put(`/sales_orders/${row.id}/cancel_finished`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.can_update_cost && {
        component: 'confirmButton',
        props: {
          label: '更新成本',
          type: 'primary',
          can: 'sales_orders.update_cost'
        },
        on: {
          confirm: async () => {
            await this.$autoLoading(this.$request.put(`/sales_orders/${row.id}/update_cost`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      }
    ].filter(Boolean);
  }

  batchActions({ selection: selectionOrigin }, { resource }) {
    // 因为有合并单元格，有的是合并的行，需要过滤掉
    const selection = selectionOrigin.filter(item => item.id);
    const exportClick = async (selection) => {
      if (selection.length === 0) {
        await this.$autoLoading(this.$request.get(`/${resource}/export`, { ...this.$route.query }));
      } else {
        await this.$autoLoading(this.$request.get(`/${resource}/export`, { ...this.$route.query, id_in: _.map(selection, item => item.id) }));
      }

      this.$message.success(this.$t('exporting'));
    }

    return [
      () => <el-button type='primary' onClick={() => exportClick(selection)}><i class='el-icon-download'></i>{this.$t('export')}</el-button>,
    ]
  }
}

export const route = [
  {
    path: '/sales_orders',
    name: 'sales_orders.index',
    component: SalesOrders,
    props: {
      type: 'index',
    }
  },
  {
    path: '/sales_orders/new',
    name: 'sales_orders.new',
    component: SalesOrders,
    props: {
      type: 'new',
    }
  },
  {
    path: '/sales_orders/:id',
    name: 'sales_orders.show',
    component: SalesOrders,
    props: {
      type: 'show',
    }
  },
  {
    path: '/sales_orders/:id/edit',
    name: 'sales_orders.edit',
    component: SalesOrders,
    props: {
      type: 'edit'
    }
  }
]
</script>
